/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {
    Link as ChakraLink,
    Icon,
    useMediaQuery,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import PropTypes from 'prop-types'
import React from 'react'
import {NavLink as NavSPALink, Link as SPALink} from 'react-router-dom'

import {colors, iconNames, styleConfigChakraKeys} from '../../constants'

const LinkType = ({
    href,
    externalUrl,
    type,
    useNavLink,
    isExternal,
    text,
    icon,
    iconStyles = {},
    iconComponent,
    children,
    buttonStyles = {},
    openLinkInNewTab,
    textUnderLine = false,
    ...props
}) => {
    const {buildUrl, locale} = useMultiSite()
    const localUrl =
        !isExternal && href.startsWith(`/${locale?.alias}/`)
            ? href
            : buildUrl(href)
    const updatedHref = isExternal ? externalUrl : localUrl
    const styles = useMultiStyleConfig(styleConfigChakraKeys.linkType)
    const [isDesktop] = useMediaQuery('(min-width: 992px)')

    return (
        <>
            {isExternal ? (
                <ChakraLink
                    href={updatedHref}
                    isExternal={openLinkInNewTab}
                    {...styles[type]}
                    {...buttonStyles}
                    {...props}
                    sx={{
                        ...(textUnderLine && isDesktop
                            ? {
                                  position: 'relative',
                                  _hover: {
                                      fontWeight: 'bold',
                                      '&::before': {
                                          width: '0%'
                                      }
                                  },
                                  paddingTop: 0,
                                  paddingBottom: 0,
                                  _focus: {
                                      backgroundColor: 'transparent'
                                  },
                                  '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      bottom: '0',
                                      left: '0',
                                      width: '100%',
                                      height: '2px',
                                      backgroundColor: '#000000',
                                      transition: 'width 0.3s ease-in-out'
                                  }
                              }
                            : !isDesktop && textUnderLine
                            ? {textDecoration: 'underline'}
                            : {})
                    }}
                >
                    {text && text}
                    {icon && <Icon as={iconNames[icon]} {...iconStyles} />}
                    {iconComponent && iconComponent}
                    {children}
                </ChakraLink>
            ) : (
                <ChakraLink
                    as={useNavLink ? NavSPALink : SPALink}
                    to={updatedHref}
                    isExternal={openLinkInNewTab}
                    {...styles[type]}
                    {...(useNavLink && {exact: true})}
                    {...buttonStyles}
                    {...props}
                    sx={{
                        ...(textUnderLine && isDesktop
                            ? {
                                  position: 'relative',
                                  _hover: {
                                      fontWeight: 'bold',
                                      '&::before': {
                                          width: '0%'
                                      }
                                  },
                                  paddingTop: '20px',
                                  paddingBottom: 0,
                                  _focus: {
                                      backgroundColor: 'transparent'
                                  },
                                  '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      bottom: '0',
                                      left: '0',
                                      width: '100%',
                                      height: '2px',
                                      backgroundColor: colors.black,
                                      transition: 'width 0.3s ease-in-out'
                                  }
                              }
                            : !isDesktop && textUnderLine
                            ? {textDecoration: 'underline'}
                            : {})
                    }}
                >
                    {text && text}
                    {icon && <Icon as={iconNames[icon]} {...iconStyles} />}
                    {iconComponent && iconComponent}
                    {children}
                </ChakraLink>
            )}
        </>
    )
}

LinkType.displayName = 'Link'

LinkType.propTypes = {
    href: PropTypes.string,
    externalUrl: PropTypes.string,
    useNavLink: PropTypes.bool,
    isExternal: PropTypes.bool,
    text: PropTypes.string,
    icon: PropTypes.string,
    iconStyles: PropTypes.object,
    iconComponent: PropTypes.object,
    children: PropTypes.node,
    textUnderLine: PropTypes.bool,
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'unstyled']),
    buttonStyles: PropTypes.object,
    openLinkInNewTab: PropTypes.bool
}

export default React.memo(LinkType)
